import { useEffect, useMemo, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { API_BASE, formatApiError, useAuth } from "../components/AuthContext";
import CommentsPanel from "../components/CommentsPanel";
import ComposeEmailDialog from "../components/ComposeEmailDialog";
import CreateTaskDialog from "../components/CreateTaskDialog";
import {
  Inbox, Mail, RefreshCw, Sparkles, Send, Copy, Archive,
  Loader2, Plug, Plug2, AlertCircle, CheckCircle2, ArchiveRestore, ListChecks,
  Paperclip, Download, Eye, X as XIcon, Bold, Italic, List as ListIcon,
  FileText, Receipt, FilePlus2, ChevronDown, Zap, Forward, PenSquare, Search, ListPlus,
} from "lucide-react";

const INTENT_COLOR = {
  question: "var(--ink)",
  document_request: "var(--accent)",
  complaint: "#B91C1C",
  invoice_inquiry: "var(--moss)",
  scheduling: "var(--accent)",
  spam: "var(--ink-3)",
  fyi: "var(--ink-3)",
  other: "var(--ink-3)",
};

const INTENT_LABEL = {
  question: "Question",
  document_request: "Doc request",
  complaint: "Complaint",
  invoice_inquiry: "Invoice query",
  scheduling: "Scheduling",
  spam: "Spam",
  fyi: "FYI",
  other: "Other",
};

const TABS = [
  { key: "untriaged", label: "Untriaged" },
  { key: "triaged", label: "Triaged" },
  { key: "archived", label: "Archived" },
];

function fmtTime(ms) {
  if (!ms) return "—";
  return new Date(Number(ms)).toLocaleString(undefined, {
    month: "short", day: "numeric", hour: "2-digit", minute: "2-digit",
  });
}

export default function EmailTriage() {
  const { state } = useAuth();
  const [params, setParams] = useSearchParams();
  const [status, setStatus] = useState(null);
  const [tab, setTab] = useState("untriaged");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const [bulkSel, setBulkSel] = useState([]);
  const [bulkTask, setBulkTask] = useState(false);
  useEffect(() => { setBulkSel([]); }, [tab]);
  const toggleBulk = (id) => setBulkSel((cur) => (cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id]));
  const [syncing, setSyncing] = useState(false);
  const [connFilter, setConnFilter] = useState("");        // connection_id filter or "" = all
  const [showAddModal, setShowAddModal] = useState(false);
  const [showCompose, setShowCompose] = useState(false);
  const [search, setSearch] = useState("");             // raw input
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  // Debounce the search box so we don't hit the API on every keystroke.
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => clearTimeout(t);
  }, [search]);

  const reloadStatus = useCallback(async () => {
    try {
      const r = await axios.get(`${API_BASE}/email-triage/status`);
      setStatus(r.data);
    } catch (_err) { /* silent: header poll */ }
  }, []);

  const reloadList = useCallback(async () => {
    setLoading(true);
    try {
      const qp = new URLSearchParams({ status: tab });
      if (connFilter) qp.set("connection_id", connFilter);
      if (debouncedSearch) qp.set("q", debouncedSearch);
      if (dateFrom) qp.set("date_from", dateFrom);
      if (dateTo) qp.set("date_to", dateTo);
      const r = await axios.get(`${API_BASE}/email-triage?${qp.toString()}`);
      setRows(r.data);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setLoading(false); }
  }, [tab, connFilter, debouncedSearch, dateFrom, dateTo]);

  useEffect(() => { reloadStatus(); }, [reloadStatus]);
  useEffect(() => { reloadList(); }, [reloadList]);

  // Handle OAuth callback redirect
  useEffect(() => {
    const connected = params.get("gmail_connected");
    const err = params.get("gmail_error");
    const errKey = params.get("key");
    const errDetail = params.get("detail");
    if (connected) { toast.success("Gmail connected"); setParams({}); reloadStatus(); }
    if (err) {
      const messages = {
        missing_env: `Production env var missing: ${errKey || "?"}. Set it on the deployment and redeploy.`,
        missing_public_url: "PUBLIC_APP_URL is not set on production. Set it to https://flowtax.io and redeploy.",
        encryption_failed: "APP_ENCRYPTION_KEY is missing or invalid on production. Set a valid Fernet key and redeploy.",
        invalid_grant: "The Google authorization code expired or was already used. Try Connect Gmail again.",
        invalid_client: "GOOGLE_CLIENT_ID or GOOGLE_CLIENT_SECRET is wrong on production. Verify against Google Cloud Console and redeploy.",
        redirect_uri_mismatch: "Google says the redirect URI doesn't match. Confirm https://flowtax.io/api/oauth/gmail/callback is whitelisted in Google Cloud Console.",
        state_invalid: "The OAuth session expired or was reused. Try Connect Gmail again.",
        missing_code: "Google didn't return an authorization code. Try Connect Gmail again.",
        timeout: "The token exchange request to Google timed out. Production network/egress issue.",
        ssl_error: "SSL/certificate error talking to Google. Production network issue.",
        network_error: "Network error talking to Google. Production pod may not have outbound internet access.",
        exchange_failed: "Gmail token exchange failed.",
      };
      const base = messages[err] || `Gmail connect failed: ${err}`;
      const full = errDetail ? `${base}\n\nException: ${errDetail}` : base;
      toast.error(full, { duration: 20000 });
      setParams({});
    }
  }, [params, setParams, reloadStatus]);

  const startConnect = async ({ kind, label }) => {
    try {
      const r = await axios.post(`${API_BASE}/oauth/gmail/login`, { kind, label });
      window.location.href = r.data.authorize_url;
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const disconnectOne = async (conn) => {
    const isOthers = conn.connected_by_email && state?.user?.email
      && conn.connected_by_email.toLowerCase() !== state.user.email.toLowerCase();
    const who = isOthers
      ? `${conn.connected_by_name || conn.connected_by_email}'s `
      : "";
    const prompt = isOthers
      ? `Force-disconnect ${who}Gmail (${conn.gmail_address})?\n\nThis revokes the access at Google. They'll need to reconnect to resume syncing. Synced emails stay.`
      : `Disconnect ${conn.gmail_address}? Synced emails stay, but no further sync until you reconnect.`;
    if (!window.confirm(prompt)) return;
    try {
      const r = await axios.delete(`${API_BASE}/email-triage/connections/${conn.connection_id}`);
      toast.success(r.data?.disconnected_by_admin
        ? `Force-disconnected ${conn.gmail_address}`
        : "Gmail disconnected");
      reloadStatus();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const sync = async () => {
    setSyncing(true);
    try {
      const url = connFilter
        ? `${API_BASE}/email-triage/connections/${connFilter}/sync`
        : `${API_BASE}/email-triage/sync`;
      const r = await axios.post(url);
      const ins = r.data.inserted ?? r.data.results?.reduce((a, x) => a + (x.inserted || 0), 0) ?? 0;
      const leadsNew = r.data.auto_leads_created ?? 0;
      const leadsAtt = r.data.auto_leads_attached ?? 0;
      toast.success(`Synced — ${ins} new${leadsNew ? `, ${leadsNew} new lead${leadsNew !== 1 ? "s" : ""}` : ""}${leadsAtt ? `, ${leadsAtt} attached` : ""}`);
      reloadStatus(); reloadList();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSyncing(false); }
  };

  const patchConnection = async (conn, patch) => {
    try {
      await axios.patch(`${API_BASE}/email-triage/connections/${conn.connection_id}`, patch);
      reloadStatus();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  // v68.35 — toggle Gmail push (Watch API + Pub/Sub) per connection.
  const togglePush = async (conn) => {
    const on = !conn.push_enabled;
    try {
      await axios.post(
        `${API_BASE}/email-triage/connections/${conn.connection_id}/watch/${on ? "start" : "stop"}`,
      );
      toast.success(on ? `Instant push enabled for ${conn.gmail_address}` : "Push disabled");
      reloadStatus();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const onAfterMutate = (updated) => {
    if (!updated) { reloadList(); return; }
    setRows((xs) => xs.map((x) => x.email_id === updated.email_id ? { ...x, ...updated } : x));
    setTimeout(reloadList, 200);
  };

  const selected = useMemo(() => rows.find((r) => r.email_id === selectedId) || (selectedId ? { email_id: selectedId } : null), [rows, selectedId]);
  useEffect(() => { const id = params.get("email_id"); if (id) setSelectedId(id); }, [params]);

  return (
    <div className="px-10 py-12 max-w-[1400px]" data-testid="email-triage-page">
      <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
        <div>
          <div className="overline mb-2">AI inbox · Claude Sonnet 4.5</div>
          <h1 className="font-heading text-4xl" style={{ fontWeight: 500 }}>
            Email <em className="italic" style={{ color: "var(--accent)" }}>triage</em>
          </h1>
          <p className="text-[var(--ink-2)] mt-2 max-w-2xl">
            Connect your Gmail. We classify each message, draft a reply, and propose a job when work is implied.
            Nothing sends until you click.
          </p>
        </div>
        <div className="flex items-center gap-3">
          {(status?.connections?.length ?? 0) > 0 ? (
            <>
              <select
                value={connFilter}
                onChange={(e) => { setConnFilter(e.target.value); setSelectedId(null); }}
                className="bg-white border border-[var(--border)] rounded-md px-3 py-2 text-[13px] font-mono"
                data-testid="triage-connection-filter"
              >
                <option value="">All inboxes</option>
                {status.connections.map((c) => (
                  <option key={c.connection_id} value={c.connection_id}>
                    {(c.label ? `${c.label} · ` : "")}{c.gmail_address}{c.kind === "shared" ? " (shared)" : ""}
                  </option>
                ))}
              </select>
              <button onClick={sync} disabled={syncing} className="btn-primary" data-testid="triage-sync-btn">
                {syncing ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
                {syncing ? "Syncing…" : "Sync"}
              </button>
              <button onClick={() => setShowCompose(true)} className="btn-secondary" data-testid="triage-compose-btn">
                <PenSquare size={14} /> New email
              </button>
              <button onClick={() => setShowAddModal(true)} className="btn-secondary" data-testid="triage-add-account-btn">
                <Plug size={14} /> Add account
              </button>
            </>
          ) : (
            <button onClick={() => setShowAddModal(true)} className="btn-primary" data-testid="triage-connect-btn">
              <Plug size={14} /> Connect Gmail
            </button>
          )}
        </div>
      </div>

      {(status?.connections?.length ?? 0) > 0 && (
        <div className="card p-4 mb-6" data-testid="triage-connections-card">
          <div className="overline mb-3 flex items-center justify-between">
            <span>Connected inboxes · {status.connections.length}</span>
            <span className="text-[var(--ink-3)] font-mono normal-case tracking-normal text-[11px]">
              {status.untriaged_count} untriaged across all
            </span>
          </div>
          <div className="space-y-2">
            {status.connections.map((c) => (
              <ConnectionRow
                key={c.connection_id}
                conn={c}
                onPatch={(patch) => patchConnection(c, patch)}
                onDisconnect={() => disconnectOne(c)}
                onTogglePush={() => togglePush(c)}
              />
            ))}
          </div>
        </div>
      )}

      {(status?.connections?.length ?? 0) === 0 && (
        <div className="card p-8 text-center" data-testid="triage-empty-disconnected">
          <Inbox size={32} className="mx-auto mb-3" style={{ color: "var(--ink-3)" }} />
          <div className="font-heading text-xl mb-1" style={{ fontWeight: 500 }}>No inboxes connected</div>
          <div className="text-[var(--ink-3)] text-sm max-w-md mx-auto">
            Connect your first Gmail account to start auto-importing leads. You can connect as many as you like —
            personal, shared <span className="font-mono">leads@</span>, or partner inboxes.
          </div>
        </div>
      )}

      {showAddModal && (
        <AddConnectionModal
          onClose={() => setShowAddModal(false)}
          onConfirm={(payload) => { setShowAddModal(false); startConnect(payload); }}
        />
      )}

      <ComposeEmailDialog
        open={showCompose}
        onClose={() => setShowCompose(false)}
        onSent={() => { setShowCompose(false); sync(); }}
      />

      {(status?.connections?.length ?? 0) > 0 && (
        <>
          <div className="flex items-center gap-2 mb-4" data-testid="triage-tabs">
            {TABS.map((t) => (
              <button
                key={t.key}
                onClick={() => { setTab(t.key); setSelectedId(null); }}
                data-testid={`triage-tab-${t.key}`}
                className={`px-3 py-1.5 rounded-full text-[12px] uppercase tracking-wider transition ${
                  tab === t.key ? "bg-[var(--ink)] text-[var(--paper)]" : "text-[var(--ink-3)] hover:text-[var(--ink)]"
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>

          {/* Gmail-style search — matches sender, recipient, subject, body,
              and resolves client/lead by name, email or mobile number. */}
          <div className="relative mb-4" data-testid="triage-search-wrap">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--ink-3)]" />
            <input
              value={search}
              onChange={(e) => { setSearch(e.target.value); setSelectedId(null); }}
              data-testid="triage-search-input"
              placeholder="Search mail — name, email, mobile, subject or words in the message"
              className="w-full bg-white border border-[var(--border)] rounded-full pl-9 pr-9 py-2.5 text-[13px] focus:outline-none focus:border-[var(--ink)]"
            />
            {search && (
              <button onClick={() => setSearch("")} data-testid="triage-search-clear"
                className="absolute right-3 top-1/2 -translate-y-1/2 text-[var(--ink-3)] hover:text-[var(--ink)]">
                <XIcon size={15} />
              </button>
            )}
          </div>

          {/* Date range + operator hint */}
          <div className="flex items-center flex-wrap gap-2 mb-4 text-[11px] text-[var(--ink-3)]" data-testid="triage-search-daterange">
            <span>Filters:</span>
            <input type="date" value={dateFrom} onChange={(e) => { setDateFrom(e.target.value); setSelectedId(null); }}
              data-testid="triage-date-from"
              className="bg-white border border-[var(--border)] rounded px-2 py-1 text-[11px] focus:outline-none focus:border-[var(--ink)]" />
            <span>to</span>
            <input type="date" value={dateTo} onChange={(e) => { setDateTo(e.target.value); setSelectedId(null); }}
              data-testid="triage-date-to"
              className="bg-white border border-[var(--border)] rounded px-2 py-1 text-[11px] focus:outline-none focus:border-[var(--ink)]" />
            {(dateFrom || dateTo) && (
              <button onClick={() => { setDateFrom(""); setDateTo(""); }} data-testid="triage-date-clear"
                className="inline-flex items-center gap-0.5 hover:text-[var(--ink)]"><XIcon size={11} /> Clear dates</button>
            )}
            <span className="ml-auto font-mono normal-case tracking-normal text-[var(--ink-3)]">
              Try: from: to: subject: has:attachment
            </span>
          </div>

          <div className="grid grid-cols-12 gap-6">
            <div className="col-span-5 space-y-2" data-testid="triage-list">
              {bulkSel.length > 0 && (
                <div className="sticky top-0 z-10 card px-3 py-2 flex items-center gap-2 text-[12px] bg-[var(--paper)]" data-testid="triage-bulk-bar">
                  <span className="font-medium" data-testid="triage-bulk-count">{bulkSel.length} selected</span>
                  <button className="text-[var(--ink-3)] hover:text-[var(--ink)]" onClick={() => setBulkSel([])} data-testid="triage-bulk-clear">Clear</button>
                  <div className="flex-1" />
                  <button className="btn-primary" onClick={() => setBulkTask(true)} data-testid="triage-bulk-task-btn">
                    <ListPlus size={13} /> Create one task
                  </button>
                </div>
              )}
              {bulkTask && (() => {
                const picked = bulkSel.map((id) => rows.find((r) => r.email_id === id) || { email_id: id });
                const cl = [...new Set(picked.map((p) => p.client_id || null))];
                return (
                  <CreateTaskDialog
                    defaults={{ title: picked.length === 1 ? `Follow up: ${picked[0].subject || "email"}`.slice(0, 240) : `Follow up on ${picked.length} emails`,
                      description: picked.map((p) => `• ${p.subject || "(no subject)"} — ${p.from_addr || ""}`).join("\n").slice(0, 4000),
                      source: { type: "emails", id: picked[0].email_id, ids: picked.map((p) => p.email_id), label: picked.length === 1 ? (picked[0].subject || "1 email").slice(0, 300) : `${picked.length} emails` },
                      ...(cl.length === 1 && cl[0] ? { parent_kind: "client", parent_id: cl[0] } : {}) }}
                    context={`${picked.length} email${picked.length === 1 ? "" : "s"}${cl.length === 1 && cl[0] ? " · same client" : ""}`}
                    onClose={() => setBulkTask(false)}
                    onCreated={() => { setBulkTask(false); setBulkSel([]); }} />
                );
              })()}
              {loading ? (
                <div className="text-[var(--ink-3)] text-sm">Loading…</div>
              ) : rows.length === 0 ? (
                <div className="card p-8 text-center text-[var(--ink-3)]" data-testid="triage-empty-list">
                  <Inbox size={20} className="mx-auto mb-2" />
                  <div className="text-sm">
                    {debouncedSearch ? `No emails match “${debouncedSearch}”.` : "No emails in this tab."}
                  </div>
                </div>
              ) : rows.map((r) => (
                <div key={r.email_id} className="flex items-start gap-2">
                <input type="checkbox" checked={bulkSel.includes(r.email_id)} onChange={() => toggleBulk(r.email_id)}
                       className="mt-5 w-3.5 h-3.5 shrink-0" title="Select for a combined task"
                       data-testid={`triage-select-${r.email_id}`} />
                <button
                  onClick={() => setSelectedId(r.email_id)}
                  data-testid={`triage-row-${r.email_id}`}
                  className={`w-full min-w-0 text-left card p-4 transition border ${
                    selectedId === r.email_id ? "border-[var(--ink)]" : bulkSel.includes(r.email_id) ? "border-[var(--ink-3)]" : "border-[var(--border)]"
                  }`}
                >
                  <div className="flex items-center justify-between text-[11px] text-[var(--ink-3)] mb-1">
                    <span className="truncate flex-1 mr-2">{r.from_addr || "(unknown sender)"}</span>
                    <span className="font-mono">{fmtTime(r.internal_date_ms)}</span>
                  </div>
                  <div className="text-[14px] font-medium text-[var(--ink)] truncate">{r.subject || "(no subject)"}</div>
                  <div className="text-[12px] text-[var(--ink-2)] mt-1 line-clamp-2">{r.snippet}</div>
                  {r.triage?.intent && (
                    <div className="mt-2 flex items-center gap-2">
                      <span className="badge" style={{ background: INTENT_COLOR[r.triage.intent] || "var(--ink-3)", color: "var(--paper)" }}>
                        {INTENT_LABEL[r.triage.intent] || r.triage.intent}
                      </span>
                      {r.reply_sent_at && (
                        <span className="text-[11px] text-[var(--moss)] inline-flex items-center gap-1">
                          <CheckCircle2 size={11} /> Replied via {r.reply_channel}
                        </span>
                      )}
                    </div>
                  )}
                </button>
                </div>
              ))}
            </div>

            <div
              className="col-span-7 sticky top-6 self-start max-h-[calc(100vh-2rem)] overflow-y-auto pr-1"
              data-testid="triage-detail-pane"
            >
              {selected ? (
                <EmailDetail key={selected.email_id} email={selected} onUpdated={onAfterMutate} />
              ) : (
                <div className="card p-10 text-center text-[var(--ink-3)]">
                  <Sparkles size={20} className="mx-auto mb-2" />
                  <div className="text-sm">Pick an email to triage.</div>
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function EmailDetail({ email, onUpdated }) {
  const [full, setFull] = useState(null);
  const [busy, setBusy] = useState(false);
  const [reply, setReply] = useState("");
  const [channel, setChannel] = useState("gmail");
  const [showCc, setShowCc] = useState(false);
  const [ccInput, setCcInput] = useState("");
  const [bccInput, setBccInput] = useState("");
  const [replyAttachments, setReplyAttachments] = useState([]);   // [{filename, content_b64, content_type, size}]
  const [previewAttachment, setPreviewAttachment] = useState(null); // {url, mime_type, filename}
  const [quoteOriginal, setQuoteOriginal] = useState(true);
  const [showForward, setShowForward] = useState(false);
  const [showTask, setShowTask] = useState(false);
  const [emailTasks, setEmailTasks] = useState([]);
  const loadTasks = useCallback(() => {
    axios.get(`${API_BASE}/tasks`, { params: { source_id: email.email_id } })
      .then((r) => setEmailTasks(r.data)).catch(() => {});
  }, [email.email_id]);
  useEffect(() => { loadTasks(); }, [loadTasks]);

  const reload = useCallback(async () => {
    try {
      const r = await axios.get(`${API_BASE}/email-triage/${email.email_id}`);
      setFull(r.data);
      setReply(r.data?.triage?.suggested_reply || "");
    } catch (err) { console.error(err); }
  }, [email.email_id]);

  useEffect(() => { reload(); }, [reload]);

  const runTriage = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API_BASE}/email-triage/${email.email_id}/triage`);
      setFull(r.data);
      setReply(r.data?.triage?.suggested_reply || "");
      toast.success("Triage complete");
      onUpdated(r.data);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  const send = async () => {
    if (!reply.trim()) { toast.error("Reply cannot be empty"); return; }
    const parseList = (raw) => raw.split(",").map((s) => s.trim()).filter(Boolean);
    setBusy(true);
    try {
      const r = await axios.post(`${API_BASE}/email-triage/${email.email_id}/reply`, {
        body: reply, channel,
        quote_original: quoteOriginal,
        cc: parseList(ccInput),
        bcc: parseList(bccInput),
        attachments: replyAttachments.map(({ filename, content_b64, content_type }) => ({
          filename, content_b64, content_type,
        })),
      });
      toast.success(`Reply sent via ${channel}`);
      setFull(r.data.email);
      onUpdated(r.data.email);
      setReplyAttachments([]); setCcInput(""); setBccInput(""); setShowCc(false);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  const onPickAttachment = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";  // allow re-picking same file
    const MAX_BYTES = 20 * 1024 * 1024;  // 20 MB hard cap to stay under Gmail's 25MB total
    for (const f of files) {
      if (f.size > MAX_BYTES) {
        toast.error(`${f.name} is over 20 MB — too big to attach`);
        continue;
      }
      try {
        const b64 = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(String(reader.result).split(",")[1] || "");
          reader.onerror = reject;
          reader.readAsDataURL(f);
        });
        setReplyAttachments((prev) => [...prev, {
          filename: f.name, content_b64: b64,
          content_type: f.type || "application/octet-stream", size: f.size,
        }]);
      } catch (err) { toast.error(`Failed to read ${f.name}`); }
    }
  };

  const removeAttachment = (idx) =>
    setReplyAttachments((prev) => prev.filter((_, i) => i !== idx));

  // v68.26 — pull a file from anywhere in the app (quote PDF, document, etc.)
  // and add it to the reply as a base64 attachment. Used by the "Attach quote"
  // and "Attach document" pickers below.
  const attachFromUrl = async (url, filename, fallback_content_type = "application/pdf") => {
    try {
      const r = await axios.get(url, { responseType: "blob" });
      const blob = r.data;
      if (blob.size > 20 * 1024 * 1024) {
        toast.error(`${filename} is over 20 MB — too big to attach`);
        return;
      }
      const b64 = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(String(reader.result).split(",")[1] || "");
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });
      setReplyAttachments((prev) => [...prev, {
        filename, content_b64: b64,
        content_type: blob.type || fallback_content_type,
        size: blob.size,
      }]);
      toast.success(`Attached ${filename}`);
    } catch (err) {
      toast.error(formatApiError(err) || `Could not attach ${filename}`);
    }
  };

  const downloadIncomingAttachment = async (att, mode = "download") => {
    try {
      const r = await axios.get(
        `${API_BASE}/email-triage/${email.email_id}/attachments/${att.attachment_id}`,
        { responseType: "blob" },
      );
      const blob = new Blob([r.data], { type: att.mime_type || r.data.type });
      const url = URL.createObjectURL(blob);
      if (mode === "preview") {
        setPreviewAttachment({ url, mime_type: att.mime_type, filename: att.filename });
        return;
      }
      const a = document.createElement("a");
      a.href = url; a.download = att.filename || "attachment";
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    } catch (err) { toast.error(formatApiError(err) || "Attachment fetch failed"); }
  };

  const closePreview = () => {
    if (previewAttachment?.url) URL.revokeObjectURL(previewAttachment.url);
    setPreviewAttachment(null);
  };

  const copyReply = async () => {
    try {
      await navigator.clipboard.writeText(reply);
      toast.success("Reply copied to clipboard");
    } catch (err) { toast.error("Copy failed"); }
  };

  const archive = async () => {
    setBusy(true);
    try {
      const endpoint = full?.status === "archived" ? "unarchive" : "archive";
      await axios.post(`${API_BASE}/email-triage/${email.email_id}/${endpoint}`);
      toast.success(full?.status === "archived" ? "Restored from archive" : "Archived");
      onUpdated(null);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  const createLead = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API_BASE}/email-triage/${email.email_id}/create-lead`);
      const lid = r.data?.lead?.lead_id;
      if (r.data?.status === "duplicate") {
        toast.success(`Matched existing lead ${lid} — opening leads board`);
      } else if (r.data?.status === "existing") {
        toast.success(`Lead already exists: ${lid}`);
      } else {
        toast.success(`Lead created: ${r.data?.lead?.name}`);
      }
      // Reload to pick up the lead_id stamp on the email
      await reload();
      if (lid) window.open(`/leads`, "_self");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  const [matchSuggestion, setMatchSuggestion] = useState(null);
  useEffect(() => {
    if (full?.email_id && !full?.lead_id) {
      axios.get(`${API_BASE}/email-triage/${full.email_id}/suggest-match`)
        .then((r) => setMatchSuggestion(r.data))
        .catch(() => setMatchSuggestion(null));
    }
  }, [full?.email_id, full?.lead_id]);

  const attachToLead = async (lead_id) => {
    setBusy(true);
    try {
      const r = await axios.post(`${API_BASE}/email-triage/${email.email_id}/attach`, { lead_id });
      toast.success(`Attached to lead ${r.data.lead.name}`);
      await reload();
      onUpdated(r.data.lead);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  const attachToClient = async (client_id) => {
    setBusy(true);
    try {
      const r = await axios.post(`${API_BASE}/email-triage/${email.email_id}/attach`, { client_id });
      toast.success(`Attached to ${r.data.lead.name}`);
      await reload();
      onUpdated(r.data.lead);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  if (!full) return <div className="card p-10 text-[var(--ink-3)] text-sm">Loading…</div>;

  const t = full.triage;

  return (
    <div className="card p-6 space-y-5" data-testid="triage-detail">
      <div>
        <div className="text-[11px] text-[var(--ink-3)] font-mono mb-1">{full.from_addr}</div>
        <h2 className="font-heading text-2xl mb-2" style={{ fontWeight: 500 }}>{full.subject}</h2>
        <div className="flex items-center justify-between gap-2">
          <div className="text-[11px] text-[var(--ink-3)] font-mono">{fmtTime(full.internal_date_ms)}</div>
          <button
            onClick={archive}
            disabled={busy}
            data-testid="triage-archive-btn-top"
            title={full.status === "archived"
              ? "Move back to triage"
              : "Mark this email as not a lead and hide it from triage"}
            className="inline-flex items-center gap-1 text-[11px] px-2.5 py-1 rounded-full border border-[var(--border)] text-[var(--ink-2)] hover:border-[var(--ink)] hover:text-[var(--ink)] disabled:opacity-50"
          >
            {full.status === "archived" ? <ArchiveRestore size={11} /> : <Archive size={11} />}
            {full.status === "archived" ? "Restore" : "Not a lead"}
          </button>
        </div>
      </div>

      <div className="border-t border-[var(--border)] pt-4">
        <div className="flex items-center justify-between mb-2">
          <div className="overline">Original message</div>
          {!full.ai_summary && (
            <button onClick={async () => {
              try {
                const r = await axios.post(`${API_BASE}/email-triage/${email.email_id}/summarize`);
                setFull((f) => ({ ...f, ai_summary: r.data.summary }));
                toast.success("Summary generated");
              } catch (err) {
                toast.error(formatApiError(err) || "Summary failed");
              }
            }} className="text-[11px] text-[var(--accent)] hover:underline inline-flex items-center gap-1"
              data-testid="triage-summarize-btn">
              <Sparkles size={11}/> Summarise
            </button>
          )}
        </div>
        {full.ai_summary && (
          <div className="mb-3 p-3 rounded-md bg-[var(--accent-light)] border-l-2 border-[var(--accent)] text-[12.5px] text-[var(--ink-2)] whitespace-pre-wrap"
               data-testid="triage-ai-summary">
            <div className="overline text-[9px] text-[var(--accent)] mb-1.5 flex items-center gap-1">
              <Sparkles size={10}/> AI summary
            </div>
            {full.ai_summary}
          </div>
        )}
        <div className="text-[13px] text-[var(--ink-2)] whitespace-pre-wrap max-h-64 overflow-y-auto"
             data-testid="triage-original-body">
          {full.text_body || full.snippet || "(empty body)"}
        </div>
        {Array.isArray(full.attachments) && full.attachments.length > 0 && (
          <div className="mt-3 space-y-1.5" data-testid="triage-attachments-list">
            <div className="overline text-[10px]">Attachments ({full.attachments.length})</div>
            {full.attachments.map((att) => {
              const previewable = (att.mime_type || "").startsWith("image/") || att.mime_type === "application/pdf";
              const kb = att.size_bytes ? Math.max(1, Math.round(att.size_bytes / 1024)) : 0;
              return (
                <div key={att.attachment_id} className="flex items-center gap-2 px-2.5 py-1.5 rounded-md border border-[var(--border)] bg-white"
                     data-testid={`triage-attachment-${att.attachment_id}`}>
                  <Paperclip size={12} className="text-[var(--ink-3)] shrink-0" />
                  <span className="text-[12px] text-[var(--ink-2)] truncate flex-1" title={att.filename}>
                    {att.filename}
                  </span>
                  {kb > 0 && <span className="text-[10px] text-[var(--ink-3)] font-mono">{kb} KB</span>}
                  {previewable && (
                    <button onClick={() => downloadIncomingAttachment(att, "preview")}
                            className="text-[11px] text-[var(--accent)] hover:underline inline-flex items-center gap-0.5"
                            data-testid={`triage-attachment-preview-${att.attachment_id}`}>
                      <Eye size={11}/> Preview
                    </button>
                  )}
                  <button onClick={() => downloadIncomingAttachment(att, "download")}
                          className="text-[11px] text-[var(--ink-3)] hover:text-[var(--ink)] inline-flex items-center gap-0.5"
                          data-testid={`triage-attachment-download-${att.attachment_id}`}>
                    <Download size={11}/> Download
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {!t ? (
        <div className="border-t border-[var(--border)] pt-4 space-y-3">
          <button onClick={runTriage} disabled={busy} className="btn-primary w-full justify-center"
                  data-testid="triage-run-btn">
            {busy ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
            {busy ? "Asking Claude…" : "Triage with AI"}
          </button>
          <div className="text-center text-[11px] text-[var(--ink-3)]">
            Optional — you can reply or forward below without triaging.
          </div>
        </div>
      ) : (
        <div className="border-t border-[var(--border)] pt-4 space-y-3" data-testid="triage-result">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="badge" style={{ background: INTENT_COLOR[t.intent] || "var(--ink-3)", color: "var(--paper)" }}>
                {INTENT_LABEL[t.intent] || t.intent}
              </span>
              <span className="text-[11px] font-mono text-[var(--ink-3)]">
                confidence {(t.confidence * 100).toFixed(0)}%
              </span>
            </div>
            {t.summary && <div className="text-[14px] text-[var(--ink)]" data-testid="triage-summary">{t.summary}</div>}
            {t.suggested_job && (
              <div className="card p-3" style={{ background: "var(--subtle)" }} data-testid="triage-suggested-job">
                <div className="overline mb-1 text-[10px]">Proposed job</div>
                <div className="text-[13px] font-medium">{t.suggested_job.title}</div>
                <div className="text-[12px] text-[var(--ink-3)] mt-1">
                  Priority: {t.suggested_job.priority} · Surfaced as a proposal on <a href="/insights" className="text-[var(--accent)] hover:underline">Insights</a>
                </div>
              </div>
            )}
            <button onClick={runTriage} disabled={busy} className="text-[12px] text-[var(--ink-3)] hover:text-[var(--accent)] inline-flex items-center gap-1"
                    data-testid="triage-rerun-btn">
              <RefreshCw size={11} /> Re-run triage
            </button>
          </div>
      )}

          <div className="border-t border-[var(--border)] pt-4 space-y-3">
            <div className="flex items-center justify-between">
              <div className="overline">Reply draft</div>
              <button onClick={() => setShowCc((v) => !v)}
                      className="text-[11px] text-[var(--ink-3)] hover:text-[var(--ink)]"
                      data-testid="triage-toggle-cc">
                {showCc ? "Hide" : "Add"} Cc/Bcc
              </button>
            </div>
            {showCc && (
              <div className="space-y-2" data-testid="triage-cc-fields">
                <input type="text" value={ccInput} onChange={(e) => setCcInput(e.target.value)}
                       placeholder="Cc — comma-separated emails"
                       className="w-full bg-white border border-[var(--border)] rounded-md px-3 py-1.5 text-[12px] focus:outline-none focus:border-[var(--ink)]"
                       data-testid="triage-reply-cc"/>
                <input type="text" value={bccInput} onChange={(e) => setBccInput(e.target.value)}
                       placeholder="Bcc — comma-separated emails"
                       className="w-full bg-white border border-[var(--border)] rounded-md px-3 py-1.5 text-[12px] focus:outline-none focus:border-[var(--ink)]"
                       data-testid="triage-reply-bcc"/>
              </div>
            )}
            <textarea
              value={reply}
              onChange={(e) => setReply(e.target.value)}
              rows={6}
              data-testid="triage-reply-textarea"
              className="w-full bg-white border border-[var(--border)] rounded-md px-3 py-2 text-[13px] focus:outline-none focus:border-[var(--ink)]"
            />
            <div className="flex items-center flex-wrap gap-2">
              <label className="text-[11px] text-[var(--ink-3)] inline-flex items-center gap-1 cursor-pointer hover:text-[var(--ink)]"
                     data-testid="triage-reply-attach-label">
                <Paperclip size={12}/> Attach file{replyAttachments.length > 0 && ` (${replyAttachments.length})`}
                <input type="file" multiple onChange={onPickAttachment} className="hidden"
                       data-testid="triage-reply-attach-input"/>
              </label>
              <TemplatePicker
                clientId={full.client_id}
                onInsert={(rendered) => setReply((r) => (r ? r + "\n\n" : "") + rendered)}
              />
              {full.lead_id && (
                <button
                  onClick={() => attachFromUrl(
                    `${API_BASE}/leads/${full.lead_id}/quote-pdf`,
                    `quote-${full.lead_id.slice(-6)}.pdf`,
                  )}
                  className="text-[11px] text-[var(--ink-3)] inline-flex items-center gap-1 hover:text-[var(--ink)]"
                  data-testid="triage-reply-attach-quote"
                  title="Attach this lead's quote PDF"
                >
                  <Receipt size={12}/> Attach quote
                </button>
              )}
              {full.client_id && (
                <DocumentPicker
                  clientId={full.client_id}
                  onPick={(doc) => attachFromUrl(
                    `${API_BASE}/documents/${doc.document_id}/download`,
                    doc.filename,
                    doc.content_type,
                  )}
                />
              )}
            </div>
            {replyAttachments.length > 0 && (
              <div className="space-y-1" data-testid="triage-reply-attachments">
                {replyAttachments.map((a, i) => (
                  <div key={`${a.filename}-${i}`} className="flex items-center gap-2 text-[11px] px-2 py-1 rounded bg-[var(--subtle)]">
                    <Paperclip size={10} className="text-[var(--ink-3)]"/>
                    <span className="flex-1 truncate" title={a.filename}>{a.filename}</span>
                    <span className="text-[var(--ink-3)] font-mono">{Math.max(1, Math.round(a.size / 1024))} KB</span>
                    <button onClick={() => removeAttachment(i)} className="text-[var(--ink-3)] hover:text-[#B91C1C]"
                            data-testid={`triage-reply-attach-remove-${i}`}>
                      <XIcon size={11}/>
                    </button>
                  </div>
                ))}
              </div>
            )}
            <div className="flex items-center gap-2">
              <label className="text-[12px] text-[var(--ink-3)]">Send via:</label>
              <button onClick={() => setChannel("gmail")}
                      className={`px-3 py-1 rounded-full text-[11px] uppercase tracking-wider ${
                        channel === "gmail" ? "bg-[var(--ink)] text-[var(--paper)]" : "text-[var(--ink-3)]"
                      }`}
                      data-testid="triage-channel-gmail">
                Gmail (as you)
              </button>
              <button onClick={() => setChannel("resend")}
                      className={`px-3 py-1 rounded-full text-[11px] uppercase tracking-wider ${
                        channel === "resend" ? "bg-[var(--ink)] text-[var(--paper)]" : "text-[var(--ink-3)]"
                      }`}
                      data-testid="triage-channel-resend">
                Resend (firm domain)
              </button>
            </div>
            <label className="flex items-center gap-2 text-[11px] text-[var(--ink-3)] cursor-pointer select-none"
                   data-testid="triage-quote-original-label">
              <input type="checkbox" checked={quoteOriginal}
                     onChange={(e) => setQuoteOriginal(e.target.checked)}
                     data-testid="triage-quote-original"/>
              Include original message below the reply (keeps the full thread)
            </label>
            <div className="flex gap-2">
              <button onClick={send} disabled={busy} className="btn-primary flex-1 justify-center"
                      data-testid="triage-send-btn">
                {busy ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
                {busy ? "Sending…" : `Send via ${channel}`}
              </button>
              <button onClick={copyReply} className="btn-secondary justify-center"
                      data-testid="triage-copy-btn">
                <Copy size={14} /> Copy
              </button>
              <button onClick={() => setShowForward(true)} disabled={busy}
                      className="btn-secondary justify-center"
                      data-testid="triage-forward-btn" title="Forward this email">
                <Forward size={14} /> Forward
              </button>
              <button onClick={() => setShowTask(true)} disabled={busy}
                      className="btn-secondary justify-center"
                      data-testid="triage-create-task-btn" title="Create a task from this email">
                <ListPlus size={14} /> Task
              </button>
              <button onClick={archive} disabled={busy} className="btn-secondary justify-center"
                      data-testid="triage-archive-btn"
                      title={full.status === "archived" ? "Move back to triage" : "Hide from triage — not a lead"}>
                {full.status === "archived" ? <ArchiveRestore size={14} /> : <Archive size={14} />}
                {full.status === "archived" ? "Restore" : "Not a lead"}
              </button>
            </div>
            {emailTasks.length > 0 && (
              <div className="text-[12px] space-y-1" data-testid="triage-email-tasks">
                <div className="overline">Tasks from this email</div>
                {emailTasks.map((t) => (
                  <a key={t.task_id} href={`/tasks?task_id=${t.task_id}`} className="flex items-center gap-2 hover:underline"
                     data-testid={`triage-email-task-${t.task_id}`}>
                    <span className={t.status === "done" ? "line-through text-[var(--ink-3)]" : ""}>{t.title}</span>
                  </a>
                ))}
              </div>
            )}
            {showTask && (
              <CreateTaskDialog
                defaults={{ title: `Follow up: ${full.subject || "email"}`.slice(0, 240),
                  description: `From ${full.from_addr || ""}\n\n${(full.snippet || "").slice(0, 500)}`,
                  source: { type: "email", id: full.email_id, label: (full.subject || "").slice(0, 300) },
                  ...(full.client_id ? { parent_kind: "client", parent_id: full.client_id }
                    : full.lead_id ? { parent_kind: "lead", parent_id: full.lead_id } : {}) }}
                context={`email “${full.subject || ""}”${full.client_id ? " · client" : full.lead_id ? " · lead" : ""}`}
                onClose={() => setShowTask(false)}
                onCreated={() => { setShowTask(false); loadTasks(); }} />
            )}
            <div className="border-t border-[var(--border)] pt-3 mt-1 space-y-2">
              <button onClick={createLead} disabled={busy || !!full.lead_id} className="text-[12px] text-[var(--accent)] hover:underline inline-flex items-center gap-1.5 disabled:text-[var(--ink-3)] disabled:no-underline"
                      data-testid="triage-create-lead-btn">
                <ListChecks size={12}/>
                {full.lead_id ? `Lead linked · ${full.lead_id}` : "Create lead from this email"}
              </button>
              {!full.lead_id && (
                <div className="text-[10px] text-[var(--ink-3)] mt-1">
                  Adds sender to the Lead pipeline with source=email + auto-fee calc. Dedupes on email match.
                </div>
              )}
              {!full.lead_id && matchSuggestion && (matchSuggestion.leads?.length > 0 || matchSuggestion.clients?.length > 0) && (
                <div className="bg-[var(--subtle)] rounded-md p-3 space-y-2" data-testid="triage-match-suggestions">
                  <div className="text-[11px] text-[var(--ink-2)] font-medium">
                    Same sender found:
                  </div>
                  {matchSuggestion.leads?.map((l) => (
                    <button key={l.lead_id} onClick={() => attachToLead(l.lead_id)} disabled={busy}
                            className="w-full text-left flex items-center justify-between gap-2 text-[12px] px-2 py-1.5 rounded hover:bg-white border border-transparent hover:border-[var(--border)]"
                            data-testid={`triage-attach-lead-${l.lead_id}`}>
                      <span className="truncate">
                        <span className="badge text-[9px] mr-1.5" style={{ background: "var(--accent)", color: "var(--paper)" }}>LEAD</span>
                        <b>{l.name}</b> <span className="text-[var(--ink-3)] font-mono">· {l.status}</span>
                      </span>
                      <span className="text-[var(--accent)] text-[10px] uppercase tracking-wider">Attach →</span>
                    </button>
                  ))}
                  {matchSuggestion.clients?.map((c) => (
                    <button key={c.client_id} onClick={() => attachToClient(c.client_id)} disabled={busy}
                            className="w-full text-left flex items-center justify-between gap-2 text-[12px] px-2 py-1.5 rounded hover:bg-white border border-transparent hover:border-[var(--border)]"
                            data-testid={`triage-attach-client-${c.client_id}`}>
                      <span className="truncate">
                        <span className="badge text-[9px] mr-1.5" style={{ background: "var(--moss)", color: "var(--paper)" }}>CLIENT</span>
                        <b>{c.name}</b>
                      </span>
                      <span className="text-[var(--accent)] text-[10px] uppercase tracking-wider">New lead →</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
            {full.reply_sent_at && (
              <div className="text-[11px] text-[var(--moss)] inline-flex items-center gap-1" data-testid="triage-reply-status">
                <CheckCircle2 size={11} /> Replied {fmtTime(new Date(full.reply_sent_at).getTime())} via {full.reply_channel}
              </div>
            )}
          </div>
          {showForward && (
            <ForwardModal emailId={email.email_id} defaultChannel={channel}
              onClose={() => setShowForward(false)}
              onSent={() => setShowForward(false)} />
          )}

      <div className="border-t border-[var(--border)] pt-4" data-testid="triage-comments-section">
        <div className="overline mb-2">Internal discussion</div>
        <CommentsPanel kind="email" entityId={email.email_id} />
      </div>

      {previewAttachment && (
        <div className="fixed inset-0 z-50 bg-[var(--ink)]/70 flex items-center justify-center p-6"
             onClick={closePreview} data-testid="triage-attachment-preview-modal">
          <div className="bg-[var(--paper)] rounded-lg w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col"
               onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between gap-3 px-4 py-2.5 border-b border-[var(--border)]">
              <div className="text-[13px] font-medium truncate">{previewAttachment.filename}</div>
              <div className="flex items-center gap-2">
                <a href={previewAttachment.url} download={previewAttachment.filename}
                   className="text-[11px] text-[var(--ink-3)] hover:text-[var(--ink)] inline-flex items-center gap-1"
                   data-testid="triage-attachment-preview-download">
                  <Download size={12}/> Download
                </a>
                <button onClick={closePreview} className="text-[var(--ink-3)] hover:text-[var(--ink)]"
                        data-testid="triage-attachment-preview-close">
                  <XIcon size={16}/>
                </button>
              </div>
            </div>
            <div className="flex-1 overflow-auto bg-[var(--subtle)] flex items-center justify-center">
              {previewAttachment.mime_type === "application/pdf" ? (
                <iframe src={previewAttachment.url} title={previewAttachment.filename}
                        className="w-full h-[80vh] border-0"/>
              ) : previewAttachment.mime_type?.startsWith("image/") ? (
                <img src={previewAttachment.url} alt={previewAttachment.filename}
                     className="max-w-full max-h-[80vh] object-contain"/>
              ) : (
                <div className="text-[12px] text-[var(--ink-3)] p-8">No inline preview available.</div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


function ConnectionRow({ conn, onPatch, onDisconnect, onTogglePush }) {
  const pushOn = !!conn.push_enabled;
  const expIso = conn.watch_expires_at;
  const pushLabel = pushOn
    ? (expIso ? `Push on · renews ${new Date(expIso).toLocaleDateString(undefined, {month:"short", day:"numeric"})}`
              : "Push on")
    : "Instant push";
  return (
    <div className="flex items-center justify-between gap-4 p-3 rounded-md border border-[var(--border)]"
         style={{ background: "var(--paper)" }}
         data-testid={`triage-conn-${conn.connection_id}`}>
      <div className="flex items-center gap-3 min-w-0">
        <Mail size={14} style={{ color: "var(--moss)" }} />
        <div className="min-w-0">
          <div className="text-[13px] text-[var(--ink)] font-medium truncate">
            {conn.label ? <span className="text-[var(--ink)]">{conn.label}</span> : null}
            {conn.label && <span className="text-[var(--ink-3)] mx-1.5">·</span>}
            <span className="font-mono text-[var(--ink-2)]">{conn.gmail_address}</span>
            {conn.kind === "shared" && (
              <span className="ml-2 badge badge-accent text-[9px]">SHARED</span>
            )}
            {pushOn && (
              <span className="ml-2 inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded
                                text-[9px] font-medium bg-[var(--accent-light)] text-[var(--accent)]"
                    data-testid={`triage-conn-push-badge-${conn.connection_id}`}>
                <Zap size={9} /> LIVE
              </span>
            )}
          </div>
          <div className="text-[10px] text-[var(--ink-3)] font-mono mt-0.5">
            {conn.last_sync_at
              ? `last sync ${new Date(conn.last_sync_at).toLocaleString(undefined, { hour: "2-digit", minute: "2-digit", month: "short", day: "numeric" })}`
              : "never synced"}
            {conn.connected_by_name && (
              <span className="ml-2">· connected by {conn.connected_by_name}</span>
            )}
          </div>
        </div>
      </div>
      <div className="flex items-center gap-3 flex-wrap text-[11px] text-[var(--ink-3)]">
        <button
          onClick={onTogglePush}
          className={`inline-flex items-center gap-1 px-2 py-1 rounded text-[11px] transition-colors ${
            pushOn ? "bg-[var(--accent-light)] text-[var(--accent)] hover:opacity-80"
                   : "border border-[var(--border)] hover:border-[var(--ink-3)] text-[var(--ink-2)]"}`}
          title={pushOn ? "Turn off Gmail push notifications" : "Enable instant Gmail push (Watch API)"}
          data-testid={`triage-conn-push-toggle-${conn.connection_id}`}
        >
          <Zap size={11} /> {pushLabel}
        </button>
        <label className="inline-flex items-center gap-1.5 cursor-pointer">
          <input
            type="checkbox"
            checked={conn.auto_sync_enabled !== false}
            onChange={(e) => onPatch({ auto_sync_enabled: e.target.checked })}
            data-testid={`triage-conn-autosync-${conn.connection_id}`}
          />
          Auto-sync (15 min)
        </label>
        <label className="inline-flex items-center gap-1.5 cursor-pointer">
          <input
            type="checkbox"
            checked={conn.auto_create_leads !== false}
            onChange={(e) => onPatch({ auto_create_leads: e.target.checked })}
            data-testid={`triage-conn-autolead-${conn.connection_id}`}
          />
          Auto-create leads
        </label>
        <label className="inline-flex items-center gap-1.5 cursor-pointer">
          <input
            type="checkbox"
            checked={!!conn.auto_triage_enabled}
            onChange={(e) => onPatch({ auto_triage_enabled: e.target.checked })}
            data-testid={`triage-conn-autotriage-${conn.connection_id}`}
          />
          AI triage
        </label>
        <button
          onClick={onDisconnect}
          className="text-[var(--ink-3)] hover:text-[var(--accent)] inline-flex items-center gap-1"
          data-testid={`triage-conn-disconnect-${conn.connection_id}`}
        >
          <Plug2 size={11} /> Disconnect
        </button>
      </div>
    </div>
  );
}


function AddConnectionModal({ onClose, onConfirm }) {
  const [kind, setKind] = useState("user");
  const [label, setLabel] = useState("");
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
         style={{ background: "rgba(26,24,20,0.45)" }}
         data-testid="triage-add-modal">
      <div className="bg-[var(--paper)] border border-[var(--border)] rounded-md max-w-md w-full p-7">
        <div className="overline mb-1">Add Gmail account</div>
        <h3 className="font-heading text-2xl mb-4" style={{ fontWeight: 500 }}>
          Connect a <em className="italic" style={{ color: "var(--accent)" }}>new inbox</em>
        </h3>

        <div className="space-y-4">
          <div>
            <label className="overline block mb-1.5">Scope</label>
            <div className="space-y-2">
              <label className="flex items-start gap-2 cursor-pointer p-3 rounded-md border border-[var(--border)] hover:bg-[var(--subtle)]">
                <input type="radio" name="kind" value="user" checked={kind === "user"}
                       onChange={() => setKind("user")} data-testid="triage-add-kind-user" className="mt-0.5" />
                <span className="text-[13px]">
                  <b>Personal</b> — only I can see emails from this account
                  <div className="text-[11px] text-[var(--ink-3)] mt-0.5">e.g. your individual partner inbox</div>
                </span>
              </label>
              <label className="flex items-start gap-2 cursor-pointer p-3 rounded-md border border-[var(--border)] hover:bg-[var(--subtle)]">
                <input type="radio" name="kind" value="shared" checked={kind === "shared"}
                       onChange={() => setKind("shared")} data-testid="triage-add-kind-shared" className="mt-0.5" />
                <span className="text-[13px]">
                  <b>Shared firm inbox</b> — everyone in the firm can see + triage
                  <div className="text-[11px] text-[var(--ink-3)] mt-0.5">e.g. <span className="font-mono">leads@firm.com</span>, <span className="font-mono">hello@</span></div>
                </span>
              </label>
            </div>
          </div>

          <div>
            <label className="overline block mb-1.5">Label <span className="text-[var(--ink-3)] normal-case tracking-normal">(optional)</span></label>
            <input
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="e.g. Leads inbox"
              className="w-full bg-white border border-[var(--border)] rounded-md px-3.5 py-2.5 text-[14px]"
              data-testid="triage-add-label"
            />
            <div className="text-[10px] text-[var(--ink-3)] mt-1">
              Used as <span className="font-mono">source_detail</span> on auto-created leads (helps you see which inbox brings the most work in Reports).
            </div>
          </div>
        </div>

        <div className="flex gap-3 mt-6">
          <button onClick={onClose} className="btn-secondary flex-1 justify-center" data-testid="triage-add-cancel">Cancel</button>
          <button onClick={() => onConfirm({ kind, label })} className="btn-primary flex-1 justify-center"
                  data-testid="triage-add-confirm">
            <Plug size={14} /> Continue to Google
          </button>
        </div>
      </div>
    </div>
  );
}

/* ==============================================================
   v68.26 — Triage reply controls: template + document pickers.
   These sit next to "Attach file" and let the user compose a fully
   populated reply without leaving the triage panel.
   ============================================================== */

function TemplatePicker({ clientId, onInsert }) {
  const [open, setOpen] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [tRes, cRes] = await Promise.all([
        axios.get(`${API_BASE}/email-templates`),
        clientId ? axios.get(`${API_BASE}/clients/${clientId}`).catch(() => null) : Promise.resolve(null),
      ]);
      setTemplates(tRes.data || []);
      // Stash client for variable rendering
      window.__triage_client__ = cRes?.data || null;
    } catch (err) { toast.error(formatApiError(err) || "Could not load templates"); }
    finally { setLoading(false); }
  };

  const render = (body) => {
    if (!body) return "";
    const c = window.__triage_client__ || {};
    // Very small variable substitution — covers the 4 fields most templates use.
    return body
      .replace(/\{\{\s*client\.name\s*\}\}/gi, c.name || "there")
      .replace(/\{\{\s*client\.first_name\s*\}\}/gi, (c.name || "").split(" ")[0] || "there")
      .replace(/\{\{\s*client\.email\s*\}\}/gi, c.primary_contact_email || "")
      .replace(/\{\{\s*firm\.name\s*\}\}/gi, c.org_name || "our firm");
  };

  return (
    <div className="relative">
      <button
        onClick={() => { if (!open) load(); setOpen((v) => !v); }}
        data-testid="triage-reply-template-btn"
        className="text-[11px] text-[var(--ink-3)] inline-flex items-center gap-1 hover:text-[var(--ink)]"
        title="Insert a saved email template"
      >
        <FilePlus2 size={12}/> Templates
        <ChevronDown size={9} className={`transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-30" onClick={() => setOpen(false)} />
          <div className="absolute z-40 mt-1 w-72 max-h-72 overflow-y-auto bg-white rounded-md shadow-lg border border-[var(--border)] py-1"
               data-testid="triage-reply-template-menu">
            {loading ? (
              <div className="px-3 py-2 text-[11px] text-[var(--ink-3)] inline-flex items-center gap-1">
                <Loader2 size={11} className="animate-spin" /> Loading…
              </div>
            ) : templates.length === 0 ? (
              <div className="px-3 py-2 text-[11px] text-[var(--ink-3)] italic">
                No templates yet. Add one in Settings → Templates.
              </div>
            ) : templates.map((t) => (
              <button
                key={t.template_id}
                onClick={() => { onInsert(render(t.body || t.body_html || "")); setOpen(false); }}
                data-testid={`triage-reply-template-${t.template_id}`}
                className="w-full text-left px-3 py-2 text-[12px] hover:bg-[var(--subtle)] focus:bg-[var(--subtle)] focus:outline-none"
              >
                <div className="font-medium truncate" style={{ color: "var(--ink)" }}>{t.name}</div>
                <div className="text-[10px] text-[var(--ink-3)] truncate">
                  {(t.subject || "").slice(0, 60) || "(no subject)"}
                </div>
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function DocumentPicker({ clientId, onPick }) {
  const [open, setOpen] = useState(false);
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API_BASE}/documents`, { params: { client_id: clientId } });
      setDocs(data || []);
    } catch (err) { toast.error(formatApiError(err) || "Could not load documents"); }
    finally { setLoading(false); }
  };

  return (
    <div className="relative">
      <button
        onClick={() => { if (!open) load(); setOpen((v) => !v); }}
        data-testid="triage-reply-doc-btn"
        className="text-[11px] text-[var(--ink-3)] inline-flex items-center gap-1 hover:text-[var(--ink)]"
        title="Attach a document from the client's library"
      >
        <FileText size={12}/> Attach document
        <ChevronDown size={9} className={`transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-30" onClick={() => setOpen(false)} />
          <div className="absolute z-40 mt-1 w-80 max-h-80 overflow-y-auto bg-white rounded-md shadow-lg border border-[var(--border)] py-1"
               data-testid="triage-reply-doc-menu">
            {loading ? (
              <div className="px-3 py-2 text-[11px] text-[var(--ink-3)] inline-flex items-center gap-1">
                <Loader2 size={11} className="animate-spin" /> Loading…
              </div>
            ) : docs.length === 0 ? (
              <div className="px-3 py-2 text-[11px] text-[var(--ink-3)] italic">
                No documents on this client yet.
              </div>
            ) : docs.map((d) => (
              <button
                key={d.document_id}
                onClick={() => { onPick(d); setOpen(false); }}
                data-testid={`triage-reply-doc-${d.document_id}`}
                className="w-full text-left px-3 py-2 text-[12px] hover:bg-[var(--subtle)] focus:bg-[var(--subtle)] focus:outline-none"
              >
                <div className="font-medium truncate" style={{ color: "var(--ink)" }}>{d.filename}</div>
                <div className="text-[10px] text-[var(--ink-3)] flex items-center gap-2">
                  <span>{d.folder || "no folder"}</span>
                  {typeof d.size === "number" && <span className="font-mono">{Math.max(1, Math.round(d.size / 1024))} KB</span>}
                </div>
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}



/* Forward an email to new recipients. Reuses the backend forward endpoint
   which appends the original message as a quoted thread. */
function ForwardModal({ emailId, defaultChannel = "gmail", onClose, onSent }) {
  const [to, setTo] = useState("");
  const [note, setNote] = useState("");
  const [channel, setChannel] = useState(defaultChannel);
  const [busy, setBusy] = useState(false);

  const send = async () => {
    const list = to.split(",").map((s) => s.trim()).filter(Boolean);
    if (list.length === 0) { toast.error("Add at least one recipient"); return; }
    setBusy(true);
    try {
      await axios.post(`${API_BASE}/email-triage/${emailId}/forward`, {
        to: list, note, channel,
      });
      toast.success(`Forwarded via ${channel}`);
      onSent();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4"
         onClick={onClose} data-testid="forward-modal">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-3 border-b border-[var(--border)]">
          <div className="text-[13px] font-semibold inline-flex items-center gap-1.5"><Forward size={14}/> Forward email</div>
          <button onClick={onClose} className="text-[var(--ink-3)] hover:text-[var(--ink)]"><XIcon size={16} /></button>
        </div>
        <div className="p-5 space-y-3">
          <div>
            <div className="overline text-[10px] mb-1.5">To</div>
            <input value={to} onChange={(e) => setTo(e.target.value)} autoFocus
              placeholder="comma-separated emails"
              data-testid="forward-to"
              className="w-full bg-white border border-[var(--border)] rounded px-3 py-2 text-[13px] focus:outline-none focus:border-[var(--ink)]" />
          </div>
          <div>
            <div className="overline text-[10px] mb-1.5">Note (optional)</div>
            <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={3}
              placeholder="Add a short note above the forwarded message…"
              data-testid="forward-note"
              className="w-full bg-white border border-[var(--border)] rounded px-3 py-2 text-[13px] focus:outline-none focus:border-[var(--ink)]" />
            <div className="text-[10px] text-[var(--ink-3)] mt-1">The original message is appended automatically.</div>
          </div>
          <div className="flex items-center gap-2">
            <label className="text-[12px] text-[var(--ink-3)]">Send via:</label>
            <button onClick={() => setChannel("gmail")}
              className={`px-3 py-1 rounded-full text-[11px] uppercase tracking-wider ${channel === "gmail" ? "bg-[var(--ink)] text-white" : "text-[var(--ink-3)]"}`}
              data-testid="forward-channel-gmail">Gmail</button>
            <button onClick={() => setChannel("resend")}
              className={`px-3 py-1 rounded-full text-[11px] uppercase tracking-wider ${channel === "resend" ? "bg-[var(--ink)] text-white" : "text-[var(--ink-3)]"}`}
              data-testid="forward-channel-resend">Resend</button>
          </div>
        </div>
        <div className="flex justify-end gap-2 px-5 py-3 border-t border-[var(--border)]">
          <button onClick={onClose} className="text-[12px] px-3 py-1.5 text-[var(--ink-2)] hover:text-[var(--ink)]">Cancel</button>
          <button onClick={send} disabled={busy} data-testid="forward-send"
            className="inline-flex items-center gap-1 text-[12px] bg-[var(--ink)] text-white rounded px-3 py-1.5 hover:opacity-90 disabled:opacity-50">
            {busy ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />} Forward
          </button>
        </div>
      </div>
    </div>
  );
}
